/**
 * DataGridToolbar - Standalone toolbar component for V2 DataGrids.
 *
 * Uses omnisearch hooks internally (no prop drilling for omnisearch state).
 * Handles manage columns popover internally.
 *
 * For standard usage, prefer OmnisearchDataGridV2 which combines this toolbar
 * with SuspenseDataGrid. Use DataGridToolbar directly for custom layouts.
 */

import FilterList from "@mui/icons-material/FilterList";
import { Box, Stack, SxProps, Theme, useTheme } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { CommandButton } from "components/CommandButton";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import { OmnisearchColumnPopover } from "components/OmnisearchColumnPopover";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { MouseEvent, useCallback, useMemo, useState } from "react";

import { useOmnisearchActions, useOmnisearchStore } from "./omnisearchStore";

// =============================================================================
// Types
// =============================================================================

export interface DataGridToolbarProps {
  // ─────────────────────────────────────────────────────────────────────────
  // Filter configuration (should be module-level constants for stability)
  // ─────────────────────────────────────────────────────────────────────────
  /** Filter keys derived from columns + additional keys */
  columnFilterKeys?: FilterKey[];
  /** Filter groups for popover organization */
  filterGroups?: FilterGroup[];
  /** Key aliases for filter mapping (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /**
   * Omnisearch keys that are valid in saved filters but are not column filter
   * keys — e.g. page-level toggles like the fulfillment "View by" selector.
   * Without this they'd be stripped by filterValidKeys when a saved filter is
   * restored. Pass a stable (module-level) array reference to avoid re-renders.
   */
  preservedFilterKeys?: string[];
  /** Tenant ID for backend queries */
  tenantId?: string;
  /** Persistence key for saved filters in localStorage */
  persistenceKey?: string;
  /** Show external filter button. Default: true */
  showFilters?: boolean;
  /** Hide omnisearch input entirely. Default: false */
  hideOmnisearch?: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Manage Columns (from parent's useGridPersistence - use useMemo/useCallback)
  // ─────────────────────────────────────────────────────────────────────────
  /** Columns for the manage columns popover */
  columns?: GridColDef[];
  /** Current column visibility model */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** Callback when column visibility changes */
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /** Show manage columns button. Auto-calculates if undefined (>= 5 non-action columns) */
  shouldShowManageColumns?: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Command Buttons (use useMemo for stability)
  // ─────────────────────────────────────────────────────────────────────────
  /** Buttons to render on the right side of the toolbar */
  commandButtons?: React.ReactNode[];
  /** Buttons to render on the left side (before omnisearch) */
  startCommandButtons?: React.ReactNode[];

  // ─────────────────────────────────────────────────────────────────────────
  // Styling
  // ─────────────────────────────────────────────────────────────────────────
  /** Custom styles for the toolbar container */
  sx?: SxProps<Theme>;
}

// =============================================================================
// Component
// =============================================================================

export function DataGridToolbar({
  columnFilterKeys,
  filterGroups,
  keyAliases,
  preservedFilterKeys,
  tenantId,
  persistenceKey,
  showFilters = true,
  hideOmnisearch = false,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  shouldShowManageColumns: shouldShowManageColumnsProp,
  commandButtons,
  startCommandButtons,
  sx,
}: DataGridToolbarProps) {
  const theme = useTheme();

  // ─────────────────────────────────────────────────────────────────────────
  // Internal omnisearch state (uses hooks directly - no prop drilling)
  // ─────────────────────────────────────────────────────────────────────────
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearch, setOmnisearchImmediate } = useOmnisearchActions();

  // ─────────────────────────────────────────────────────────────────────────
  // Manage Columns Popover State (internal)
  // ─────────────────────────────────────────────────────────────────────────
  const [columnListAnchorEl, setColumnListAnchorEl] = useState<HTMLButtonElement | null>(null);
  const showColumnList = Boolean(columnListAnchorEl);

  const handleColumnListOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setColumnListAnchorEl(event.currentTarget);
  }, []);

  const handleColumnListClose = useCallback(() => {
    setColumnListAnchorEl(null);
  }, []);

  const handleToggleColumn = useCallback(
    (field: string) => {
      if (!columnVisibilityModel || !onColumnVisibilityModelChange) return;
      const newModel = {
        ...columnVisibilityModel,
        [field]: field in columnVisibilityModel ? !columnVisibilityModel[field] : false,
      };
      onColumnVisibilityModelChange(newModel);
    },
    [columnVisibilityModel, onColumnVisibilityModelChange]
  );

  // Auto-calculate shouldShowManageColumns if not provided
  const shouldShowManageColumns = useMemo(() => {
    if (shouldShowManageColumnsProp !== undefined) {
      return shouldShowManageColumnsProp;
    }
    if (!columns) return false;
    return columns.filter((c) => c.type !== "actions").length >= 5;
  }, [shouldShowManageColumnsProp, columns]);

  // Filter out actions column for the manage columns popover
  const manageableColumns = useMemo(
    () => columns?.filter((col) => col.headerName !== "actions" && col.field !== "actions") ?? [],
    [columns]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Filter Button Render (stable reference)
  // ─────────────────────────────────────────────────────────────────────────
  const renderFilterButton = useCallback(
    (onClick: (event: MouseEvent<HTMLButtonElement>) => void) => (
      <CommandButton onClick={onClick}>
        <FilterList sx={{ mr: 1 }} fontSize="small" />
        Filters
      </CommandButton>
    ),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={sx}>
      <Stack
        sx={{
          marginBottom: theme.spacing(3),
          gap: theme.spacing(1.5),
        }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
      >
        {/* Left section: startCommandButtons + Omnisearch */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: { xs: "100%", lg: "inherit" },
            maxWidth: "1022px",
            flexGrow: 1,
          }}
          gap={theme.spacing(1.5)}
        >
          {startCommandButtons}
          {!hideOmnisearch && (
            <Omnisearch
              omnisearch={omnisearch}
              setOmnisearch={setOmnisearch}
              setOmnisearchImmediate={setOmnisearchImmediate}
              columnFilterKeys={columnFilterKeys}
              filterGroups={filterGroups}
              keyAliases={keyAliases}
              preservedFilterKeys={preservedFilterKeys}
              showFilterButtonInside={false}
              showExternalFilterButton={showFilters}
              renderFilterButton={renderFilterButton}
              tenantId={tenantId}
              persistenceKey={persistenceKey}
            />
          )}
        </Stack>

        {/* Right section: Manage Columns + commandButtons */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: theme.spacing(1.5),
            flexWrap: "wrap",
          }}
        >
          {shouldShowManageColumns &&
            columns &&
            columnVisibilityModel &&
            onColumnVisibilityModelChange && (
              <>
                <CommandButton onClick={handleColumnListOpen}>Manage Columns</CommandButton>
                {showColumnList && (
                  <OmnisearchColumnPopover
                    showColumnList={showColumnList}
                    anchorEl={columnListAnchorEl}
                    handleCloseColumnList={handleColumnListClose}
                    columns={manageableColumns}
                    handleToggleColumn={handleToggleColumn}
                    selectedColumns={columnVisibilityModel}
                    setSelectedColumns={onColumnVisibilityModelChange}
                  />
                )}
              </>
            )}
          {commandButtons}
        </Stack>
      </Stack>
    </Box>
  );
}
